import React, { useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { StokvelTerms } from './StokvelTerms';
import { rand } from './format';

const ACKS = [
  'The December EFT payout schedule into the dedicated "Savings Stokvel" account structure.',
  'The tax treatment of capital vs. interest earnings and Employer Contributions.',
  'The strict Emergency Withdrawal criteria.',
  'The Early Termination Penalties (forfeiture of interest, capital penalty fees and unvested Employer Contributions) upon resignation or dismissal prior to December.',
  'The Company Offset Clause, operating alongside my Employment Contract, permitting the offset of business debts against my Stokvel funds.',
];

export const StokvelApplyDialog = ({ open, onOpenChange, tiers, onDone }) => {
  const [amount, setAmount] = useState(null);
  const [accepted, setAccepted] = useState(false);
  const [authorised, setAuthorised] = useState(false);
  const [signature, setSignature] = useState('');
  const [idNumber, setIdNumber] = useState('');
  const [saving, setSaving] = useState(false);

  const canSubmit = amount && accepted && authorised && signature.trim().length >= 2;

  const submit = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/stokvel/apply`, {
        monthly_amount: amount, accept_terms: true,
        signature_name: signature.trim(), id_number: idNumber.trim() || null,
      });
      toast.success('Application submitted — management will review it');
      onOpenChange(false);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not submit application');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="stokvel-apply-dialog">
        <DialogHeader><DialogTitle>Join Application &amp; Payroll Deduction Authorisation</DialogTitle></DialogHeader>
        <div className="space-y-5">
          <div>
            <Label className="text-sm font-semibold">1. Choose your monthly contribution</Label>
            <p className="text-xs text-stone-500 mb-2">Fixed for the full savings cycle (Jan–Nov). It cannot be changed or paused.</p>
            <div className="grid grid-cols-3 gap-2">
              {tiers.map(t => (
                <button key={t} type="button" onClick={() => setAmount(t)} data-testid={`stokvel-tier-${t}`}
                  className={`rounded-lg border px-3 py-3 text-center transition-colors ${amount === t ? 'border-amber-600 bg-amber-50 text-amber-900 ring-2 ring-amber-200' : 'border-stone-200 hover:border-stone-400'}`}>
                  <div className="text-lg font-bold">{rand(t)}</div>
                  <div className="text-xs text-stone-500">per month</div>
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-sm font-semibold">2. Read the Terms &amp; Conditions</Label>
            <div className="mt-2 border border-stone-200 rounded-lg px-3 max-h-64 overflow-y-auto"><StokvelTerms /></div>
          </div>
          <div className="space-y-3 bg-stone-50 border border-stone-200 rounded-lg p-3">
            <label className="flex gap-2 items-start text-sm cursor-pointer">
              <Checkbox checked={authorised} onCheckedChange={v => setAuthorised(!!v)} data-testid="stokvel-authorise-checkbox" className="mt-0.5" />
              <span>I authorise The Merchant Coffee to deduct {amount ? <b>{rand(amount)}</b> : 'the selected amount'} from my net salary on monthly payday.</span>
            </label>
            <label className="flex gap-2 items-start text-sm cursor-pointer">
              <Checkbox checked={accepted} onCheckedChange={v => setAccepted(!!v)} data-testid="stokvel-accept-terms-checkbox" className="mt-0.5" />
              <span>
                I agree to the full Merchant Coffee Savings Stokvel Agreement, voluntarily elect to join, and confirm I have read and understood:
                <ul className="list-disc ml-5 mt-1 text-xs text-stone-600 space-y-0.5">{ACKS.map(a => <li key={a}>{a}</li>)}</ul>
              </span>
            </label>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Electronic signature — type your full name *</Label>
              <Input value={signature} onChange={e => setSignature(e.target.value)} data-testid="stokvel-signature-input" placeholder="Full names" />
            </div>
            <div>
              <Label className="text-xs">ID number (optional)</Label>
              <Input value={idNumber} onChange={e => setIdNumber(e.target.value)} data-testid="stokvel-id-number-input" />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="stokvel-apply-cancel">Cancel</Button>
            <Button disabled={!canSubmit || saving} onClick={submit} data-testid="stokvel-apply-submit" className="bg-stone-800 hover:bg-stone-900 text-white">
              {saving ? 'Submitting…' : 'Sign & submit application'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
